import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";

const TokenomicsSection = () => {
  const tokenomicsData = [
    { 
      name: "Community Brodrop", 
      percentage: 30, 
      description: "Open mint, flat pricing",
      color: "hsl(240, 15%, 25%)"
    },
    { 
      name: "Commons Vault", 
      percentage: 20, 
      description: "Grants, tools, charity",
      color: "hsl(240, 20%, 35%)"
    },
    { 
      name: "Liquidity Lock", 
      percentage: 15, 
      description: "SOL pair, LP burned",
      color: "hsl(240, 25%, 45%)"
    },
    { 
      name: "Builder Guild", 
      percentage: 15, 
      description: "Pay devs & designers. Coordinated by trusted builders for development efforts",
      color: "hsl(245, 30%, 55%)"
    },
    { 
      name: "Bro Distribution", 
      percentage: 10, 
      description: "Quests, airdrops and ecosystem participation incentives",
      color: "hsl(250, 35%, 65%)"
    },
    { 
      name: "Meme Ops", 
      percentage: 10, 
      description: "Shared pool for ecosystem tools and ops (multisig-managed), CT raids, IRL stunts",
      color: "hsl(255, 40%, 75%)"
    },
  ];

  const cumulativePercentage = (index: number) => {
    return tokenomicsData.slice(0, index).reduce((sum, item) => sum + item.percentage, 0);
  };

  const createPieSlice = (percentage: number, cumulativePercent: number) => {
    const startAngle = (cumulativePercent / 100) * 360 - 90;
    const endAngle = ((cumulativePercent + percentage) / 100) * 360 - 90;
    
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const largeArcFlag = percentage > 50 ? 1 : 0;
    
    const x1 = 150 + 120 * Math.cos(startAngleRad);
    const y1 = 150 + 120 * Math.sin(startAngleRad);
    const x2 = 150 + 120 * Math.cos(endAngleRad);
    const y2 = 150 + 120 * Math.sin(endAngleRad);
    
    return `M 150 150 L ${x1} ${y1} A 120 120 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  return (
    <section id="tokenomics" className="py-24 px-4 bg-muted/20">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-foreground mb-6">
            <span className="text-secondary">TOKENOMICS</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            $TOP1 Token Allocations = Community Buckets (NOT Investment Pools)
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Pie Chart */}
          <div className="flex justify-center">
            <div className="relative">
              <svg width="300" height="300" className="transform">
                {tokenomicsData.map((item, index) => (
                  <g key={index}>
                    <path
                      d={createPieSlice(item.percentage, cumulativePercentage(index))}
                      fill={item.color}
                      stroke="hsl(var(--background))"
                      strokeWidth="2"
                      className="hover:opacity-80 transition-opacity"
                    />
                    {/* Percentage labels */}
                    <text
                      x={150 + 80 * Math.cos(((cumulativePercentage(index) + item.percentage / 2) / 100) * 2 * Math.PI - Math.PI / 2)}
                      y={150 + 80 * Math.sin(((cumulativePercentage(index) + item.percentage / 2) / 100) * 2 * Math.PI - Math.PI / 2)}
                      textAnchor="middle"
                      className="text-xs font-bold fill-white"
                      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                    >
                      {item.percentage}%
                    </text>
                    {/* Vault names */}
                    <text
                      x={150 + 95 * Math.cos(((cumulativePercentage(index) + item.percentage / 2) / 100) * 2 * Math.PI - Math.PI / 2)}
                      y={150 + 95 * Math.sin(((cumulativePercentage(index) + item.percentage / 2) / 100) * 2 * Math.PI - Math.PI / 2)}
                      textAnchor="middle"
                      className="text-xs font-semibold fill-current text-foreground"
                      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                    >
                      {item.name.split(' ')[0]}
                    </text>
                  </g>
                ))}
                <circle
                  cx="150"
                  cy="150"
                  r="40"
                  fill="hsl(var(--background))"
                  stroke="hsl(var(--border))"
                  strokeWidth="2"
                />
                <text
                  x="150"
                  y="155"
                  textAnchor="middle"
                  className="text-sm font-bold fill-current text-foreground"
                >
                  $TOP1
                </text>
              </svg>
            </div>
          </div>

          {/* Legend */}
          <div className="space-y-4">
            {tokenomicsData.map((item, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div 
                  className="w-6 h-6 rounded-full flex-shrink-0 mt-1"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h3 className="text-lg font-semibold text-foreground">{item.name}</h3>
                    <Badge variant="secondary" className="text-lg font-bold">
                      {item.percentage}%
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Information Cards */}
        <div className="grid md:grid-cols-2 gap-8 mt-16">
          <Card className="border-secondary/20">
            <CardHeader>
              <CardTitle className="text-secondary text-xl">Deflationary Mechanism</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground text-lg font-semibold mb-3">
                2% TRANSFER TAX TO FUND COMMUNITY EVENTS AND BURN EVENTS.
              </p>
              <p className="text-muted-foreground">
                $TOP1 applies a small burn on every buy to support long-term sustainability. 
                Sell/transfer fees fund meme campaigns, collectibles, and culture-aligned tools — not distributions or returns.
              </p>
            </CardContent>
          </Card>

          <Card className="border-secondary/20">
            <CardHeader>
              <CardTitle className="text-secondary text-xl">Community Governance</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground text-lg font-semibold mb-3">
                NON-CUSTODIAL & TRANSPARENT
              </p>
              <p className="text-muted-foreground">
                All resource buckets are publicly managed by community-chosen signers. 
                No funds are held by centralized entities or foundations. Each bucket is governed transparently by the community.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Banner */}
        <Card className="mt-12 border-2 border-secondary/30">
          <CardContent className="p-8 text-center">
            <p className="text-2xl font-bold text-foreground mb-4">
              $TOP1 ISN'T JUST A TOKEN—IT'S A COMMITMENT. A MOVEMENT. A BROTHERHOOD.
            </p>
            <p className="text-muted-foreground">
              All resource buckets are publicly managed by community-chosen signers. No funds are held by centralized entities or foundations.
            </p>
          </CardContent>
        </Card>

        {/* Legal Disclaimer */}
        <div className="mt-12 p-6 bg-muted/30 rounded-lg border border-muted">
          <p className="text-sm text-muted-foreground text-center">
            <strong>Legal Disclaimer:</strong> $TOP1 is a community token designed for participation in Bromentum initiatives. 
            It is not an investment, does not represent ownership, and carries no expectation of financial return. 
            Resources collected via the Community Mint are used to fund bot development, meme campaigns, and on-chain coordination tools, 
            all transparently managed via community multisig wallets.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TokenomicsSection;